// utils/calculatePrice.ts
import type { WebCatalogProduct, WebCatalogProductDuration } from '../services/EveryDoggyAPITypes';

type Target = 'perDay' | 'perWeek' | 'perMonth' | 'per3Month' | 'per6Month' | 'perYear';
type Source = 'auto' | 'amount' | 'trial' | 'intro';
type RoundMode = 'floor' | 'round';

interface CalcOptions {
  source?: Source;
  target: Target;
  defaultPrice?: number;
  calculateFromIntro?: boolean;
  decimals?: number;
  roundMode?: RoundMode;
}

const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const DAYS_PER_3_MONTH = 90;
const DAYS_PER_6_MONTH = 180;
const DAYS_PER_YEAR = 365;

function toNumber(value?: string | null): number | undefined {
  if (value == null) return undefined;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeUnit(u?: string | null): 'day' | 'week' | 'month' | 'year' | undefined {
  if (!u) return undefined;
  const s = u.toLowerCase().trim();
  if (s === 'day' || s === 'days' || s === 'd') return 'day';
  if (s === 'week' || s === 'weeks' || s === 'w') return 'week';
  if (s === 'month' || s === 'months' || s === 'm') return 'month';
  if (s === 'year' || s === 'years' || s === 'y') return 'year';
  return undefined;
}

function roundBy(value: number, decimals = 2, mode: RoundMode = 'floor') {
  const k = 10 ** decimals;
  return mode === 'round' ? Math.round(value * k) / k : Math.floor(value * k) / k;
}

function durationToDays(d?: WebCatalogProductDuration): number | undefined {
  if (!d?.value) return undefined;
  const u = normalizeUnit(d.unit);
  if (!u) return undefined;
  switch (u) {
    case 'day': return d.value;
    case 'week': return d.value * DAYS_PER_WEEK;
    case 'month': return d.value * DAYS_PER_MONTH;
    case 'year': return d.value * DAYS_PER_YEAR;
  }
}

function trialToDays(d?: WebCatalogProductDuration, trial_period_days?: number | null): number | undefined {
  if (trial_period_days && trial_period_days > 0) return trial_period_days;
  if (d?.trial_period_value) {
    const u = normalizeUnit(d.trial_period_unit);
    if (!u) return undefined;
    switch (u) {
      case 'day': return d.trial_period_value;
      case 'week': return d.trial_period_value * DAYS_PER_WEEK;
      case 'month': return d.trial_period_value * DAYS_PER_MONTH;
      case 'year': return d.trial_period_value * DAYS_PER_YEAR;
    }
  }
  return undefined;
}

function pickBaseAmountAndDays(
  product?: WebCatalogProduct | null,
  source: Source = 'auto',
  calculateFromIntro?: boolean,
): { amount?: number; days?: number } {

  if (!product) return { amount: undefined, days: undefined };

  const subDays = durationToDays(product.duration);
  const trialDays = trialToDays(product.duration, product.trial_period_days);

  if (source === 'auto') {
    if (calculateFromIntro) {
      const tAmount = toNumber(product.trial_amount);
      if (tAmount != null && trialDays != null) return { amount: tAmount, days: trialDays };

      const iAmount = toNumber(product.intro_amount);
      if (iAmount != null && subDays != null) return { amount: iAmount, days: subDays };
    }
    return { amount: toNumber(product.amount), days: subDays };
  }

  if (source === 'trial') {
    return { amount: toNumber(product.trial_amount), days: trialDays };
  }

  if (source === 'intro') {
    return { amount: toNumber(product.intro_amount), days: subDays };
  }

  return { amount: toNumber(product.amount), days: subDays };
}

export function calculatePrice(
  product: WebCatalogProduct | null | undefined,
  opts: CalcOptions,
): number {
  const {
    source = 'auto',
    target,
    defaultPrice,
    calculateFromIntro,
    decimals = 2,
    roundMode = 'floor',
  } = opts;

  const { amount, days } = pickBaseAmountAndDays(product, source, calculateFromIntro);

  if (amount == null || days == null || days <= 0) return defaultPrice ?? 0;

  const perDay = amount / days;

  let result: number;
  switch (target) {
    case 'perDay':
      result = perDay;
      break;
    case 'perWeek':
      result = perDay * DAYS_PER_WEEK;
      break;
    case 'perMonth':
      result = perDay * DAYS_PER_MONTH;
      break;
    case 'per3Month':
      result = perDay * DAYS_PER_3_MONTH;
      break;
    case 'per6Month':
      result = perDay * DAYS_PER_6_MONTH;
      break;
    case 'perYear':
      result = perDay * DAYS_PER_YEAR;
      break;
    default:
      result = perDay;
  }

  return roundBy(result, decimals, roundMode);
}
